import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChatCircleDots, PaperPlaneRight, X, CaretLeft, MagnifyingGlass, UsersThree, User, Plus, LockKey, Trash } from "@phosphor-icons/react";

function initials(name) {
  return (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}
function fmt(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("people");
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [unread, setUnread] = useState({ total: 0, by_contact: {} });
  const lastSeen = useRef(new Date().toISOString());

  // DM state
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  // Group state
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupMsgs, setGroupMsgs] = useState([]);
  const groupSeq = useRef(0);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [selMembers, setSelMembers] = useState({});

  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const scrollDown = () => setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 40);

  useEffect(() => { api.get("/chat/contacts").then(({ data }) => setContacts(data)).catch(() => {}); }, []);

  // DM unread badge
  useEffect(() => {
    const poll = async () => { try { const { data } = await api.get("/chat/unread", { params: { after: lastSeen.current } }); setUnread(data); } catch (e) {} };
    poll(); const id = setInterval(poll, 5000); return () => clearInterval(id);
  }, []);

  // groups list
  useEffect(() => {
    if (!open) return;
    const poll = async () => { try { const { data } = await api.get("/groups"); setGroups(data); } catch (e) {} };
    poll(); const id = setInterval(poll, 6000); return () => clearInterval(id);
  }, [open]);

  // DM messages
  useEffect(() => {
    if (!open || !active) return;
    const fetchMsgs = async () => {
      try { const { data } = await api.get("/chat", { params: { with: active.id } }); setMessages(data); scrollDown(); } catch (e) {}
    };
    fetchMsgs(); const id = setInterval(fetchMsgs, 3500); return () => clearInterval(id);
  }, [open, active]);

  // Group messages (ephemeral, incremental by seq)
  useEffect(() => {
    if (!open || !activeGroup) return;
    const fetchMsgs = async () => {
      try {
        const { data } = await api.get(`/groups/${activeGroup.id}/messages`, { params: { after: groupSeq.current } });
        if (data.length) {
          groupSeq.current = data[data.length - 1].seq;
          setGroupMsgs((prev) => [...prev, ...data]);
          scrollDown();
        }
      } catch (e) {
        if (e.response?.status === 404) { toast.error("Group was closed"); setActiveGroup(null); }
      }
    };
    fetchMsgs(); const id = setInterval(fetchMsgs, 3000); return () => clearInterval(id);
  }, [open, activeGroup]);

  const unreadTotal = unread.total;

  const openContact = (c) => {
    setActive(c); setMessages([]);
    lastSeen.current = new Date().toISOString();
    setUnread((u) => ({ total: Math.max(0, u.total - (u.by_contact[c.id] || 0)), by_contact: { ...u.by_contact, [c.id]: 0 } }));
  };

  const openGroup = (g) => { setActiveGroup(g); setGroupMsgs([]); groupSeq.current = 0; };

  const sendDM = async () => {
    const t = text.trim(); if (!t || !active) return; setText("");
    try { const { data } = await api.post("/chat", { to: active.id, text: t }); setMessages((m) => [...m, data]); scrollDown(); } catch (e) {}
  };
  const sendGroup = async () => {
    const t = text.trim(); if (!t || !activeGroup) return; setText("");
    try { await api.post(`/groups/${activeGroup.id}/messages`, { text: t }); } catch (e) { toast.error("Could not send"); }
  };
  const onSubmit = (e) => { e.preventDefault(); activeGroup ? sendGroup() : sendDM(); };

  const createGroup = async () => {
    const member_ids = Object.keys(selMembers).filter((k) => selMembers[k]);
    if (!newName.trim() || member_ids.length === 0) { toast.error("Add a name and at least one member"); return; }
    try {
      const { data } = await api.post("/groups", { name: newName.trim(), member_ids });
      setCreating(false); setNewName(""); setSelMembers({});
      setGroups((g) => [data, ...g.filter((x) => x.id !== data.id)]);
      openGroup(data);
      toast.success("Secret group created");
    } catch (e) { toast.error("Could not create group"); }
  };

  const closeGroup = async (g) => {
    if (!window.confirm(`Close "${g.name}"? All its messages are wiped instantly and permanently.`)) return;
    try { await api.delete(`/groups/${g.id}`); setActiveGroup(null); setGroups((gs) => gs.filter((x) => x.id !== g.id)); toast.success("Group closed & wiped"); } catch (e) {}
  };

  if (!user) return null;

  const filtered = contacts.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase()));
  const inConvo = active || activeGroup;
  const convoMsgs = activeGroup ? groupMsgs : messages;

  return (
    <>
      {!open && (
        <button data-testid="chat-toggle-btn" onClick={() => setOpen(true)}
          className="fixed z-50 bottom-5 right-5 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/20 flex items-center justify-center transition-transform duration-200 hover:scale-105 active:scale-95">
          <ChatCircleDots size={26} weight="fill" />
          {unreadTotal > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-accent text-white text-[11px] font-medium flex items-center justify-center">{unreadTotal > 9 ? "9+" : unreadTotal}</span>
          )}
        </button>
      )}

      {open && (
        <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-5 sm:right-5 sm:w-[380px]">
          <div className="mx-2 sm:mx-0 rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden h-[74vh] sm:h-[540px] animate-in-up">
            {/* header */}
            <div className="flex items-center justify-between px-3 h-14 bg-primary text-primary-foreground shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                {inConvo ? (
                  <>
                    <button data-testid="chat-back-btn" onClick={() => { setActive(null); setActiveGroup(null); }} className="opacity-90 hover:opacity-100"><CaretLeft size={20} /></button>
                    <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center text-[11px] font-medium">
                      {activeGroup ? <UsersThree size={16} weight="fill" /> : initials(active.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-none truncate">{activeGroup ? activeGroup.name : active.name}</p>
                      <p className="text-[11px] opacity-70 mt-0.5 capitalize flex items-center gap-1">
                        {activeGroup ? <><LockKey size={11} weight="fill" /> secret · {activeGroup.members.length} members</> : active.role}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2"><ChatCircleDots size={20} weight="fill" /><p className="text-sm font-medium">Messages</p></div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {activeGroup && activeGroup.created_by === user.id && (
                  <button data-testid="group-close-btn" onClick={() => closeGroup(activeGroup)} className="opacity-80 hover:opacity-100" title="Close & wipe group"><Trash size={18} /></button>
                )}
                <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="opacity-80 hover:opacity-100"><X size={20} /></button>
              </div>
            </div>

            {/* list view (people or groups) */}
            {!inConvo && (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex p-2 gap-1 border-b border-border">
                  <button data-testid="chat-tab-people" onClick={() => setTab("people")} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm transition-colors ${tab === "people" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}><User size={16} /> People</button>
                  <button data-testid="chat-tab-groups" onClick={() => setTab("groups")} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm transition-colors ${tab === "groups" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}><UsersThree size={16} /> Groups</button>
                </div>

                {tab === "people" && (
                  <>
                    <div className="p-2.5 border-b border-border">
                      <div className="relative">
                        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input data-testid="chat-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people…" className="w-full pl-9 pr-3 py-2 text-sm rounded-full bg-secondary outline-none" />
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {filtered.map((c) => (
                        <button key={c.id} data-testid={`chat-contact-${c.id}`} onClick={() => openContact(c)} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/50 transition-colors text-left">
                          <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center text-xs font-medium shrink-0">{initials(c.name)}</div>
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{c.name}</p><p className="text-xs text-muted-foreground truncate capitalize">{c.role}</p></div>
                          {unread.by_contact[c.id] > 0 && <span className="h-5 min-w-5 px-1 rounded-full bg-accent text-white text-[11px] flex items-center justify-center">{unread.by_contact[c.id]}</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {tab === "groups" && !creating && (
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="p-2.5">
                      <button data-testid="new-group-btn" onClick={() => setCreating(true)} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-primary/40 text-primary text-sm hover:bg-primary/5 transition-colors"><Plus size={16} /> New secret group</button>
                    </div>
                    <div className="px-3 pb-2 flex items-start gap-1.5 text-[11px] text-muted-foreground"><LockKey size={13} weight="fill" className="mt-0.5 shrink-0" /> Group messages are never saved — no history, wiped from memory automatically.</div>
                    <div className="flex-1 overflow-y-auto">
                      {groups.length === 0 && <p className="text-center text-xs text-muted-foreground mt-6">No groups yet.</p>}
                      {groups.map((g) => (
                        <button key={g.id} data-testid={`group-${g.id}`} onClick={() => openGroup(g)} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/50 transition-colors text-left">
                          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><UsersThree size={18} weight="fill" /></div>
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{g.name}</p><p className="text-xs text-muted-foreground truncate">{g.members.length} members</p></div>
                          <LockKey size={14} className="text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "groups" && creating && (
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="p-3 border-b border-border">
                      <input data-testid="group-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Group name" className="w-full px-3 py-2 text-sm rounded-lg bg-secondary outline-none" />
                      <p className="text-[11px] text-muted-foreground mt-2">Select members</p>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {contacts.map((c) => (
                        <label key={c.id} data-testid={`group-member-${c.id}`} onClick={() => setSelMembers((p) => ({ ...p, [c.id]: !p[c.id] }))}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${selMembers[c.id] ? "bg-primary/5" : "hover:bg-secondary/50"}`}>
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${selMembers[c.id] ? "bg-primary text-primary-foreground" : "bg-accent/15 text-accent"}`}>{initials(c.name)}</div>
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{c.name}</p><p className="text-xs text-muted-foreground truncate capitalize">{c.role}</p></div>
                          <div className={`h-4 w-4 rounded-full border ${selMembers[c.id] ? "bg-primary border-primary" : "border-muted-foreground/40"}`} />
                        </label>
                      ))}
                    </div>
                    <div className="p-3 border-t border-border flex gap-2">
                      <button onClick={() => { setCreating(false); setSelMembers({}); setNewName(""); }} className="flex-1 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancel</button>
                      <button data-testid="create-group-submit" onClick={createGroup} className="flex-1 py-2.5 rounded-lg text-sm bg-primary text-primary-foreground">Create</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* conversation (DM or group) */}
            {inConvo && (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-secondary/30">
                  {activeGroup && <div className="text-center text-[11px] text-muted-foreground bg-card border border-border rounded-full px-3 py-1 mx-auto w-fit flex items-center gap-1"><LockKey size={12} weight="fill" /> Secret chat · messages are not stored</div>}
                  {convoMsgs.length === 0 && <p className="text-center text-xs text-muted-foreground mt-6">No messages yet. Say hello 👋</p>}
                  {convoMsgs.map((m) => {
                    const mine = m.from_id === user.id;
                    return (
                      <div key={m.id || m.seq} data-testid="chat-message" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className="max-w-[78%] flex flex-col">
                          <div className={`px-3 py-2 rounded-2xl text-sm ${mine ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-card border border-border rounded-tl-sm"}`}>
                            {activeGroup && !mine && <p className="text-[11px] font-medium text-accent mb-0.5">{m.from_name}</p>}
                            <p className="whitespace-pre-wrap break-words">{m.text}</p>
                          </div>
                          <span className={`text-[10px] text-muted-foreground mt-0.5 px-1 ${mine ? "text-right" : ""}`}>{fmt(m.created_at)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={onSubmit} className="flex items-center gap-2 p-3 border-t border-border bg-card shrink-0">
                  <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 px-3 py-2.5 text-sm rounded-full bg-secondary outline-none focus:ring-2 focus:ring-ring/40" />
                  <button data-testid="chat-send-btn" type="submit" disabled={!text.trim()} className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 transition-transform duration-150 active:scale-90"><PaperPlaneRight size={18} weight="fill" /></button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
